<template>
  <!-- PC 端 -->
  <div
    @click="quoteAction"
    class="web-quote-layer quote-layer"
    :style="`left: ${left}px; top: ${top}px`"
    v-if="quoteMenuShowFlag && !isWap"
  >
    {{ $t('quote') }}
  </div>

  <!-- M 端 -->
  <div
    class="wap-quote-layer quote-layer"
    :style="`left: ${left}px; top: ${top}px`"
    v-if="quoteMenuShowFlag && isWap"
  >
    <p @click.stop.prevent="quoteAction">{{ $t('quote') }}</p>
    <p @click.stop.prevent="onCopy">{{ $t('copy') }}</p>
  </div>
</template>
<script setup>
import { formatQuoteOrderList, attributeRegExp } from '@/utils/formatRichText';
import { copyText, getSelectionPosition } from '@/utils';
import { storeToRefs } from 'pinia';
import { useSystemStore } from '@/stores/system';
import { useNavigationStore } from '@/stores/navigation';

const systemStore = useSystemStore();
const navigationStore = useNavigationStore();
const { isWap, isWeb } = storeToRefs(systemStore);
const { noticeHeight, navigationHeight } = storeToRefs(navigationStore)
const { proxy } = getCurrentInstance();
const emit = defineEmits(['sendQuoteHtml']);
const props = defineProps({
  editorType: {
    type: String,
    default: 'comment'
  }
});

const quoteMenuShowFlag = ref(false);
const quoteDom = ref(null);
const left = ref(null);
const top = ref(null);
const quote = ref('');
const clipboardText = ref('');
const isLongPressing = ref(false)

const getParents = (el, parentSelector) => {
  if (parentSelector === undefined) {
    parentSelector = document;
  }
  let parents = [el];
  let p = el.parentNode;
  while (p !== parentSelector) {
    let o = p;
    parents.push(o);
    p = o.parentNode;
  }
  parents.push(parentSelector);
  return parents;
};

const getCurrentSelect = () => {
  function i(e) {
    let list = getParents(e);
    return list.some((v) => v.nodeName === 'ARTICLE');
  }
  function c(e) {
    let n = i(e.startContainer),
      o = i(e.endContainer);
    return n && o;
  }

  let selectionObj = null,
    rangeObj = null;
  let selectedText = '',
    selectedHtml = '';

  // 处理兼容性
  if (window.getSelection) {
    // 现代浏览器
    // 获取text
    selectionObj = window.getSelection();
    selectedText = selectionObj.toString();

    //  获取html
    rangeObj = selectionObj.getRangeAt(0);
    if (!c(rangeObj)) {
      return {
        text: '',
        html: ''
      };
    }
    var docFragment = rangeObj.cloneContents();
    var tempDiv = document.createElement('div');
    tempDiv.appendChild(docFragment);
    selectedHtml = tempDiv.innerHTML;
  } else if (document.selection) {
    // 非主流浏览器IE
    selectionObj = document.selection;
    rangeObj = selectionObj.createRange();
    if (!c(rangeObj)) {
      return {
        text: '',
        html: ''
      };
    }
    selectedText = rangeObj.text;
    selectedHtml = rangeObj.htmlText;
  }
  // 移除播放器
  const vituralDom = document.createElement('div');
  vituralDom.innerHTML = selectedHtml;
  if (vituralDom.getElementsByClassName('plyr').length) {
    Array.from(vituralDom.getElementsByClassName('plyr')).forEach((i) =>
      i.remove()
    );
  }
  return {
    text: vituralDom.innerText.trim(),
    html: vituralDom.innerHTML.trim()
  };
};

const getDom = (e) => {
  let dom = null;
  try {
    dom = getCurrentSelect().html;
    clipboardText.value = getCurrentSelect().text;
  } catch {}
  if (dom?.startsWith('<li ') && dom?.endsWith('</li>')) {
    dom = `<ol>${dom}</ol>`;
  }
  if (dom?.startsWith('<li>') && dom?.endsWith('</li>')) {
    dom = `<ul>${dom}</ul>`;
  }
  dom = dom
    ?.replaceAll('<a', '<span')
    .replaceAll('</a>', '</span>')
    .replaceAll('\n', '');
  quoteDom.value = dom?.replaceAll('&nbsp;', '');
};

const scrollToEditor = () => {
  const eidtorName = `${props.editorType}-editor`;
  tinymce.get(eidtorName).container.scrollIntoView({
    block: 'center'
  });
};

const focusTiny = () => {
  const eidtorName = `${props.editorType}-editor`;
  if (tinymce.get(eidtorName)) {
    tinymce.get(eidtorName).focus();
    tinymce
      .get(eidtorName)
      .selection.select(tinymce.get(eidtorName).getBody(), true);
    tinymce.get(eidtorName).selection.collapse(false);
  }
};

const formatQuote = (html) => {
  const regex = new RegExp(`<([a-z]+)([^>]*\\bolive\\b[^>]*)>(.*?)</\\1>`, 'gi');
  const oliveDom = html.match(regex) || []
  oliveDom.forEach(live => {
    const dataValue = attributeRegExp({
      tag: 'img', 
      key: 'data-value', 
      regString: live
    })
    html = html.replace(live, `<img data-type="file" data-value="${dataValue}"/>`)
  })
  let imgList = html.match(/<img [^>]*>/gi) || [];
  imgList.forEach((img) => {
    if (img.includes('file')) {
      const dataValue = attributeRegExp({
        tag: 'img', 
        key: 'data-value', 
        regString: img
      })
      html = html.replace(img, `<div data-imgId="${dataValue}">[Picture]</div>`);
    }
  });
  return formatQuoteOrderList('<div>' + html + '</div>');
};

const quoteAction = () => {
  quoteMenuShowFlag.value = false;
  quote.value = '<div>' + quoteDom.value + '</div>';
  if (!isWap.value) {
    focusTiny();
    scrollToEditor();
  }
  // 显示文本
  emit('sendQuoteHtml', formatQuote(quote.value));
};

const removeQuoteDialog = (e) => {
  if (!e.target.classList.contains('quote-layer')) {
    quoteMenuShowFlag.value = false;
  }
};

const onCopy = () => {
  quoteMenuShowFlag.value = false;
  copyText(clipboardText.value).then((res) => {
    proxy.$toast({
      message: proxy.$t('copyToast'),
      duration: 2000
    });
  });
};

const getQuoteText = () => {
  if (document.querySelector('article')) {
    if (isWap.value) {
      // 监听触摸开始（按下）
      document.querySelector('article').ontouchstart = (event) => {
        isLongPressing.value = false;
          // 启动长按计时器
          setTimeout(() => {
            isLongPressing.value = true;
          }, 500); // 假设长按阈值为500ms
      }
      document.querySelector('article').oncontextmenu = (e) => {
        e.preventDefault()
        if (isLongPressing.value) {
          getDom(e);
          setMenuPosition(e)
        }
      }
    } else {
      document.querySelector('article').onmouseup = (e) => {
        getDom(e);
        setMenuPosition(e)
      };
    }
  }
};

const setMenuPosition = (e) => {
  setTimeout(() => {
    if (quoteDom.value) {
      const {pageX, pageY} = getSelectionPosition()
      const articleOffsetLeft = document.querySelector('article').getBoundingClientRect().left
      left.value = isWap.value ? pageX : pageX - articleOffsetLeft;
      top.value = isWap.value ? pageY + 20 : (pageY - noticeHeight.value - navigationHeight.value - 60);
      quoteMenuShowFlag.value = true;
    }
  });
}

onMounted(() => {
  document.body.addEventListener('click', removeQuoteDialog);
  getQuoteText();
});

onUnmounted(() => {
  document.body.removeEventListener('click', removeQuoteDialog);
});
</script>

<style scoped lang="postcss">
.quote-layer {
  position: absolute;
  box-shadow: 0px 5px 12px rgba(0, 0, 0, 0.08);
  border-radius: var(--radius-sm);
  font-size: 14px;
  line-height: 20px;
}
.web-quote-layer {
  border: 1px solid var(--line);
  padding: 12px 24px;
  background-color: #fff;
  cursor: pointer;
}
.wap-quote-layer {
  padding: 12px 0;
  background-color: var(--background-selection);
  color: var(--text-re-primary);
  p {
    padding: 8px 24px;
  }
}
</style>
